import { useRef, useState } from 'react';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import { minusSignSVG, plusSignSVG, tickSVG } from '../../../lib/allCustomSVGs';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { request_package_subscription } from '../../../lib/api/ai-tracker/subscriptions/make_package_subscriptions_api_call';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { setIsPaymentPopupOpen } from '../../../store/reducers/uiSlice';
import getCurrencySymbol from '../../../lib/helper-functions/getCurrencySymbol';
import manage_package_subscriptions_api_call from '../../../lib/api/ai-tracker/subscriptions/manage_package_subscriptions_api_call';
import { setTaraSubscriptionDetails } from '../../../store/reducers/userSlice';
import { setMessage } from '../../../store/reducers/appSlice';
import { getCheckoutFailureMessage } from '../../../lib/api/ai-tracker/subscriptions/subscription-error-message';

/** The fewest brands an organisation may buy: its brand count, at least 5. */
export const getMinimumSeats = (brandCount?: number) =>
  Math.max(5, typeof brandCount === 'number' ? brandCount : 0);

type Props = {
  isMonthly: boolean;
  purchased_seat_count?: number;
  packageDetails: {
    id: number;
    name: string;
    annual_slug: string;
    monthly_slug: string;
    monthly_price: string;
    annual_price: string;
    monthly_price_number: number;
    annual_price_number: number;
    description: string;
    features: string[];
    is_most_popular: boolean;
    currency: string;
    subscription_id?: number;
    next_billing_date?: string;
    is_active?: boolean;

    // New agency-specific fields
    monthly_per_seat_price: string;
    monthly_per_seat_price_number: number;
    annual_per_seat_price: string;
    annual_per_seat_price_number: number;
  };
  isCancelMode?: boolean;
  isUpgradable?: boolean;
  setIsSupportPopupOpen?: (value: boolean) => void;
};

const AgencyPackageItem = ({
  isMonthly,
  packageDetails,
  isCancelMode = false,
  isUpgradable = false,
  setIsSupportPopupOpen,
  purchased_seat_count,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const allShops =
    useSelector((state: RootState) => state.user?.profile?.all_shops) || [];
  const allShopsCount = allShops?.length;

  const [loading, setLoading] = useState<'pending' | 'initial'>('initial');
  // A second click lands before React has re-rendered the disabled button,
  // so the in-flight request is also tracked outside state. It stays set
  // while the browser is on its way to the checkout page.
  const requestInFlight = useRef(false);
  const startRequest = () => {
    if (requestInFlight.current) {
      return false;
    }
    requestInFlight.current = true;
    setLoading('pending');
    return true;
  };
  const endRequest = () => {
    requestInFlight.current = false;
    setLoading('initial');
  };
  // The minimum is the organisation's brand count, never below 5. It is not
  // rounded to a step of 5: 14 brands may buy exactly 14.
  const minAllowedSeats = getMinimumSeats(allShopsCount);
  const initialNumberOfSeats = () =>
    Math.max(
      minAllowedSeats,
      typeof purchased_seat_count === 'number' ? purchased_seat_count : 0
    );
  const [numberOfSeats, setNumberOfSeats] = useState<number>(
    initialNumberOfSeats()
  );
  // Shown under the button that was clicked: the toast can sit behind the
  // plans dialog, so the reason must also appear where the user is looking.
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  const showCheckoutError = (serverMessage?: string) => {
    const message = getCheckoutFailureMessage(
      serverMessage,
      t('Checkout-StartFailed')
    );
    setCheckoutError(message);
    dispatch(setMessage({ message, messageType: 'error' }));
  };

  const onMakeSubscription = async () => {
    if (numberOfSeats < minAllowedSeats) {
      return;
    }
    if (!startRequest()) {
      return;
    }
    setCheckoutError(null);

    try {
      const result = await request_package_subscription(
        isMonthly ? packageDetails.monthly_slug : packageDetails.annual_slug,
        numberOfSeats,
        { hideServerToast: true }
      );

      if (!result.ok) {
        // Keep the plans open so the user can retry, and say why nothing
        // happened: the server's reason when it gave one.
        endRequest();
        showCheckoutError(result.message);
        return;
      }

      const res = result.data;
      const checkoutUrl =
        res?.payment_details?.payment_details?._links?.checkout?.href;
      if (res && checkoutUrl && typeof checkoutUrl === 'string') {
        // Stay disabled and loading while the browser leaves for checkout.
        window.location.href = checkoutUrl;
      } else {
        endRequest();
        dispatch(setIsPaymentPopupOpen(false));
      }
    } catch (error) {
      console.error('Error making subscriptions:', error);
      endRequest();
      showCheckoutError();
    }
  };

  const onManageSubscription = async () => {
    if (!startRequest()) {
      return;
    }
    try {
      const res = await manage_package_subscriptions_api_call(
        packageDetails?.subscription_id ?? 0
      );
      if (
        res &&
        res?.data?.portal_url &&
        typeof res?.data?.portal_url === 'string'
      ) {
        window.location.href = res?.data?.portal_url;
      } else {
        endRequest();
      }
    } catch (error) {
      console.error('Error cancelling subscriptions:', error);
      endRequest();
    }
  };

  const minimumSeatsText = () =>
    minAllowedSeats > 5
      ? t('MinimumBrandsYouHave', {
          min: minAllowedSeats,
          count: allShopsCount,
        })
      : t('Minimum5Shops');

  const getFinalPrice = () => {
    const pricePerSeat = isMonthly
      ? packageDetails?.monthly_per_seat_price_number
      : packageDetails?.annual_per_seat_price_number;

    const total =
      typeof pricePerSeat === 'number' && pricePerSeat > 0
        ? pricePerSeat * numberOfSeats
        : 0;

    return `${getCurrencySymbol(packageDetails.currency)}${total}`;
  };

  return (
    <div
      data-is-most-popular={packageDetails.is_most_popular?.toString()}
      data-is-active-subscription={
        packageDetails.is_active && numberOfSeats === initialNumberOfSeats()
          ? 'true'
          : 'false'
      }
      className='agencyPackageItem'
      data-is-disabled={(
        isCancelMode &&
        !packageDetails.is_active &&
        !isUpgradable
      ).toString()}
    >
      {/* {packageDetails.is_most_popular && (
        <div className='agencyPackageItem__badge'>{t('MostPopular')}</div>
      )} */}
      {isCancelMode &&
        packageDetails.is_active &&
        numberOfSeats === initialNumberOfSeats() && (
          <div
            data-is-active-subscription={'true'}
            className='agencyPackageItem__badge'
          >
            {t('Active')}
          </div>
        )}

      <div className='agencyPackageItem-content'>
        <div className='agencyPackageItem-content-top'>
          <h2 className='agencyPackageItem__title'>
            {packageDetails.name + ' ' + (!isMonthly ? '(Annual)' : '')}
          </h2>
          {isCancelMode &&
            packageDetails.is_active &&
            numberOfSeats === initialNumberOfSeats() &&
            packageDetails.next_billing_date && (
              <div className='agencyPackageItem__billing-date collapseList__item--top-left-tag collapseList__item--top-left-tag-yellow'>
                {t('NextBillingDate')}:{' '}
                <b> {packageDetails.next_billing_date?.split(' ')[0]}</b>
              </div>
            )}
          <div
            className='agencyPackageItem__prices'
            style={
              isCancelMode &&
              (!packageDetails.is_active ||
                numberOfSeats !== initialNumberOfSeats())
                ? { marginTop: '4.6rem' }
                : {}
            }
          >
            <AnimatePresence exitBeforeEnter>
              {isMonthly ? (
                <motion.div
                  key='monthly'
                  initial={{ opacity: 0.1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0.1 }}
                  transition={{ duration: 0.3 }}
                  className='agencyPackageItem__prices--monthly'
                >
                  <h2>
                    {`${getCurrencySymbol(packageDetails.currency)}${
                      packageDetails.monthly_per_seat_price_number *
                      numberOfSeats
                    }`}
                  </h2>
                  <p>{t('/month')}</p>
                </motion.div>
              ) : (
                <motion.div
                  key='annual'
                  initial={{ opacity: 0.1 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0.1 }}
                  transition={{ duration: 0.3 }}
                  className='agencyPackageItem__prices--annual'
                >
                  {/* Non-discounted annual total */}
                  <h3>
                    {`${getCurrencySymbol(packageDetails.currency)}${
                      packageDetails.monthly_per_seat_price_number *
                      numberOfSeats *
                      12
                    }`}
                  </h3>

                  {/* Discounted total */}
                  <h2>
                    {`${getCurrencySymbol(packageDetails.currency)}${
                      packageDetails.annual_per_seat_price_number *
                      numberOfSeats *
                      12
                    }`}
                  </h2>
                  <p>{t('/year')}</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <p className='agencyPackageItem__description'>
            {/* {packageDetails.description} */}
            {`${
              isMonthly
                ? packageDetails.monthly_per_seat_price
                : `${getCurrencySymbol(packageDetails.currency)}${
                    packageDetails.annual_per_seat_price_number * 12
                  }`
            } ${t('PerBrand')} • ${t('Billed')} ${
              isMonthly ? t('monthly') : t('annually')
            }`}
          </p>

          <div className='agencyPackageItem__shop'>
            <p className='agencyPackageItem__shop--title'>
              {t('NumberOfShops')}
            </p>

            <div className='agencyPackageItem__shop--input'>
              <button
                disabled={
                  numberOfSeats <= minAllowedSeats ||
                  (isCancelMode && !packageDetails.is_active && !isUpgradable)
                }
                type='button'
                onClick={() => {
                  setNumberOfSeats((prev) =>
                    Math.max(minAllowedSeats, prev - 5)
                  );
                }}
                className='agencyPackageItem__shop--input-minus'
                style={{
                  cursor:
                    numberOfSeats <= minAllowedSeats ||
                    (isCancelMode && !packageDetails.is_active && !isUpgradable)
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                <CustomSVGs svg={minusSignSVG} />
              </button>
              <div className='agencyPackageItem__shop--input-count'>
                <span>{numberOfSeats}</span>
                <p>{t('Shops')}</p>
              </div>
              <button
                disabled={
                  numberOfSeats >= 100 ||
                  (isCancelMode && !packageDetails.is_active && !isUpgradable)
                }
                type='button'
                onClick={() => {
                  setNumberOfSeats((prev) => Math.min(100, prev + 5));
                }}
                className='agencyPackageItem__shop--input-plus'
                style={{
                  cursor:
                    numberOfSeats >= 100 ||
                    (isCancelMode && !packageDetails.is_active && !isUpgradable)
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                <CustomSVGs svg={plusSignSVG} />
              </button>
            </div>

            <p className='agencyPackageItem__shop--info'>
              {minimumSeatsText()}
            </p>
          </div>
        </div>

        {/* 
        "NumberOfShops": "Number of Brands",
        "Shops": "Brands",
        "Minimum5Shops": "Minimum 5 brands",
        "CancelAnytime": "Cancel anytime",
        "NoSetupFee": "No setup fee",
        "14DayTrial": "14-day free trial",
        */}

        <div className='agencyPackageItem__features'>
          {packageDetails.features.map((feature, index) => (
            <div key={index} className='agencyPackageItem__features--item'>
              <div className='agencyPackageItem__features--item-icon'>
                <CustomSVGs svg={tickSVG} />
              </div>
              <div className='agencyPackageItem__features--item-text'>
                {feature}
              </div>
            </div>
          ))}
        </div>
      </div>
      {!isCancelMode && (
        <div className='agencyPackageItem__button'>
          <MainSquareButton
            onClick={(e) => {
              e.preventDefault();
              onMakeSubscription();
            }}
            type={'button'}
            disabled={loading === 'pending' || numberOfSeats < minAllowedSeats}
            /* after the GetStarted text we should show the final value to pay by considering the isMonthly per_seat_price and numberOf Seats, do the cal calculation and show the final payable price to the user. only the value no need to show how we got it*/
            value={loading === 'pending' ? t('Loading') : `${t('GetStarted')}`}
            normal={true}
          />
        </div>
      )}
      {isCancelMode &&
        packageDetails.is_active &&
        numberOfSeats === initialNumberOfSeats() && (
          <div className='agencyPackageItem__button'>
            <MainSquareButton
              onClick={(e) => {
                e.preventDefault();
                onManageSubscription();
              }}
              type={'button'}
              disabled={loading === 'pending'}
              value={
                loading === 'pending' ? t('Loading') : t('ManageSubscription')
              }
              normal={true}
            />
          </div>
        )}
      {isCancelMode &&
        (isUpgradable || numberOfSeats > initialNumberOfSeats()) && (
          <div className='agencyPackageItem__button'>
            <MainSquareButton
              onClick={(e) => {
                e.preventDefault();
                onMakeSubscription();
              }}
              type={'button'}
              disabled={
                loading === 'pending' || numberOfSeats < minAllowedSeats
              }
              value={
                loading === 'pending' ? t('Loading') : t('UpgradeSubscription')
              }
              normal={true}
            />
          </div>
        )}

      {checkoutError && (
        <p
          className='agencyPackageItem__error'
          role='alert'
          data-testid='agency-checkout-error'
          style={{
            color: '#d32f2f',
            fontSize: '1.3rem',
            margin: '0.8rem 1.6rem 0',
            textAlign: 'center',
          }}
        >
          {checkoutError}
        </p>
      )}

      {/* <div className='agencyPackageItem__footer'>
        <p className='agencyPackageItem__footer--text'>
          {t('CancelAnytime')} • {t('NoSetupFee')} • {t('14DayTrial')}
        </p>
      </div> */}
    </div>
  );
};

export default AgencyPackageItem;
